/**
 * Custom Data Grid toolbar: Search (server-side), Columns, Filters, Export.
 * Uses MUI Data Grid standard toolbar building blocks.
 * Pass search/date config via slotProps.toolbar.
 *
 * Mobile: search is full-width on its own row; action buttons wrap below
 * without overlapping the grid (toolbar must not use flex-shrink-to-1px).
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  useGridRootProps,
} from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ruRU, enUS } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';

const DATE_FORMAT_DISPLAY_RU = 'DD.MM.YYYY';
const DATE_FORMAT_DISPLAY_EN = 'DD/MM/YYYY';
/** Backend findRentByDate expects yyyy-MM-dd'T'HH:mm:ss.SSS */
const DATE_FORMAT_API_START = 'YYYY-MM-DDT00:00:00.000';
const DATE_FORMAT_API_END = 'YYYY-MM-DDT23:59:59.999';

function ToolbarSearch({ searchValue, onSearchChange, onSearchSubmit, placeholder }) {
  const { t } = useTranslation();
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && typeof onSearchSubmit === 'function') {
      e.preventDefault();
      onSearchSubmit(searchValue ?? e.target.value);
    }
  };
  return (
    <TextField
      size="small"
      placeholder={placeholder ?? t('common.search')}
      value={searchValue ?? ''}
      onChange={(e) => typeof onSearchChange === 'function' && onSearchChange(e.target.value)}
      onKeyDown={handleKeyDown}
      sx={{
        width: { xs: '100%', sm: 'auto' },
        minWidth: { xs: 0, sm: 180 },
        flex: { xs: '1 1 100%', sm: '1 1 180px' },
        maxWidth: '100%',
        '@media (min-width: 600px)': { minWidth: 260, flex: '1 1 260px' },
        '@media (min-width: 960px)': { minWidth: 320, flex: '1 1 320px' },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      inputProps={{ 'aria-label': t('common.searchTable') }}
    />
  );
}

ToolbarSearch.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  placeholder: PropTypes.string,
};

function RentsDateFilterButton({ onApply, loading, compact }) {
  const { t, i18n } = useTranslation();
  const isRu = (i18n.language || 'en').startsWith('ru');
  const dateLocale = isRu ? 'ru' : 'en';
  const dateFormat = isRu ? DATE_FORMAT_DISPLAY_RU : DATE_FORMAT_DISPLAY_EN;
  const pickerLocaleText = isRu
    ? ruRU.components.MuiLocalizationProvider.defaultProps.localeText
    : enUS.components.MuiLocalizationProvider.defaultProps.localeText;
  const [anchor, setAnchor] = React.useState(null);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);

  const handleOpen = (e) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  const handleApply = () => {
    if (typeof onApply !== 'function' || !startDate || !endDate) return;
    const dStart = dayjs(startDate);
    const dEnd = dayjs(endDate);
    onApply({
      StartDate: dStart.isValid() ? dStart.startOf('day').format(DATE_FORMAT_API_START) : String(startDate).slice(0, 10),
      EndDate: dEnd.isValid() ? dEnd.endOf('day').format(DATE_FORMAT_API_END) : String(endDate).slice(0, 10),
    });
    handleClose();
  };

  const open = Boolean(anchor);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={handleOpen}
        disabled={!!loading}
        aria-label={t('common.filterByDate')}
        sx={compact ? { minWidth: 0, px: 1 } : undefined}
      >
        {compact ? null : t('common.date')}
      </Button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 280 }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={dateLocale}
            localeText={pickerLocaleText}
          >
            <DatePicker
              label={t('common.startDate')}
              value={startDate}
              onChange={setStartDate}
              format={dateFormat}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label={t('common.endDate')}
              value={endDate}
              onChange={setEndDate}
              format={dateFormat}
              minDate={startDate || undefined}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
          <Button variant="contained" onClick={handleApply} disabled={!startDate || !endDate}>
            {t('common.apply')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

RentsDateFilterButton.propTypes = {
  onApply: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  compact: PropTypes.bool,
};

/**
 * Custom toolbar for table pages. Renders:
 * - Optional server-side search input (from slotProps.toolbar)
 * - Columns button (MUI)
 * - Filters button (MUI)
 * - Optional Rents date filter (when slotProps.toolbar.rentDateFilter) — standard MUI DatePicker(s) + FilterRentsByDate API
 * - Export (MUI)
 */
function DataGridTableToolbar(props) {
  const { className, ...other } = props;
  const rootProps = useGridRootProps();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const toolbarProps = rootProps.slotProps?.toolbar || {};
  const {
    searchValue,
    onSearchChange,
    onSearchSubmit,
    searchPlaceholder,
    rentDateFilter,
    onRentDateFilterApply,
    loading,
  } = toolbarProps;

  const showSearch = typeof onSearchSubmit === 'function';
  const showRentDateFilter = rentDateFilter && typeof onRentDateFilterApply === 'function';

  return (
    <GridToolbarContainer
      className={className}
      sx={{
        flex: '0 0 auto',
        flexWrap: 'wrap',
        gap: 1,
        rowGap: 1,
        minHeight: 'auto',
        height: 'auto',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'flex-start',
        py: 0.75,
        px: 0.5,
        boxSizing: 'border-box',
      }}
      {...other}
    >
      {showSearch && (
        <ToolbarSearch
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onSearchSubmit={onSearchSubmit}
          placeholder={searchPlaceholder}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          width: { xs: '100%', sm: 'auto' },
          flex: { xs: '1 1 100%', sm: '0 1 auto' },
          minWidth: 0,
        }}
      >
        {!rootProps.disableColumnSelector && <GridToolbarColumnsButton />}
        {!rootProps.disableColumnFilter && <GridToolbarFilterButton />}
        {showRentDateFilter && (
          <RentsDateFilterButton
            onApply={onRentDateFilterApply}
            loading={loading}
            compact={isCompact}
          />
        )}
        <GridToolbarExport csvOptions={{ fileName: rootProps.label ?? 'export' }} />
      </Box>
    </GridToolbarContainer>
  );
}

DataGridTableToolbar.propTypes = {
  className: PropTypes.string,
};

export default DataGridTableToolbar;
